import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Camera, Upload, Loader2, Check, AlertTriangle, Pencil, X, Trash2, Plus, ScanLine,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const MATERIAL_TYPES_DEFAULT = ['Overburden', 'Coal', 'Shale'];

export default function TripCardScanner({ isOpen, onClose, onSaved, materialTypes = MATERIAL_TYPES_DEFAULT }) {
  const [step, setStep] = useState('upload'); // upload | reviewing | saving | done
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const fileInputRef = useRef(null);

  const reset = () => {
    setStep('upload');
    setImagePreview(null);
    setImageFile(null);
    setExtracted(null);
    setSavedCount(0);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    setImageFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const handleExtract = async () => {
    if (!imageFile) return;
    setExtracting(true);
    const fd = new FormData();
    fd.append('file', imageFile);
    try {
      const res = await axios.post(`${API}/production/scan-trip-card`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      const data = res.data;
      // Normalise trips — assign default material if blank
      data.trips = (data.trips || []).map((t, i) => ({
        ...t,
        material: t.material || materialTypes[0] || 'Overburden',
        _id: `trip-${i}`,
        _edited: false,
      }));
      setExtracted(data);
      setStep('reviewing');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Extraction failed — try a clearer image');
    } finally {
      setExtracting(false);
    }
  };

  const updateTrip = (id, field, value) => {
    setExtracted(ex => ({
      ...ex,
      trips: ex.trips.map(t => t._id === id ? { ...t, [field]: value, _edited: true } : t),
    }));
  };

  const removeTrip = (id) => {
    setExtracted(ex => ({ ...ex, trips: ex.trips.filter(t => t._id !== id) }));
  };

  const addBlankTrip = () => {
    setExtracted(ex => ({
      ...ex,
      trips: [...ex.trips, {
        _id: `trip-new-${Date.now()}`, serial: ex.trips.length + 1,
        l_time: '', ul_time: '', machine_no: '', material: materialTypes[0] || 'Overburden', _edited: true,
      }],
    }));
  };

  const handleSaveAll = async () => {
    if (!extracted) return;
    setSaving(true);
    let count = 0;
    const errors = [];

    // Build individual trip entries
    const { date, shift, door_no, starting_km, closing_km, starting_hm, closing_hm } = extracted;

    // Group trips by machine_no for excavator mapping
    const trips = extracted.trips.filter(t => t.l_time && t.ul_time);

    for (const trip of trips) {
      try {
        const payload = {
          date: date || '',
          shift: shift || 'B',
          dumper_door_no: door_no || '',
          excavator_door_no: trip.machine_no || '',
          start_time: trip.l_time || '',
          end_time: trip.ul_time || '',
          trips_count: 1,
          material_type: trip.material || materialTypes[0] || 'Overburden',
          distance_km: starting_km && closing_km
            ? Math.round(((closing_km - starting_km) / trips.length) * 100) / 100
            : null,
          cum_volume: null,
        };
        await axios.post(`${API}/production/trips`, payload, { headers: authHeaders() });
        count++;
      } catch (err) {
        errors.push(`Trip ${trip.serial}: ${err.response?.data?.detail || err.message}`);
      }
    }

    // Save hour meter entry if KM/HM data present
    if (door_no && (starting_hm || closing_hm)) {
      try {
        await axios.post(`${API}/production/hour-meters`, {
          date, shift: shift || 'B', door_no,
          starting_km: starting_km || null, closing_km: closing_km || null,
          starting_hm: starting_hm || null, closing_hm: closing_hm || null,
          operator_name: extracted.operator_name || '',
        }, { headers: authHeaders() });
      } catch {
        // hour meter save is best-effort
      }
    }

    setSaving(false);
    setSavedCount(count);

    if (errors.length > 0) {
      toast.warning(`Saved ${count} trips. ${errors.length} failed.`);
    } else {
      toast.success(`Successfully saved ${count} trips for Door No. ${door_no}!`);
    }

    setStep('done');
    onSaved?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => { if (!open) handleClose(); }}>
      <DialogContent className="max-w-4xl flex flex-col max-h-[92vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-blue-600" />
            Scan Trip Card — AI Auto-Entry
            {step === 'reviewing' && extracted && (
              <Badge variant="outline" className="ml-2 text-xs">
                Door No. {extracted.door_no} · {extracted.trips?.length} trips
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto">

          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="space-y-4 p-1">
              <div
                className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all"
                onClick={() => fileInputRef.current?.click()}
              >
                {imagePreview ? (
                  <div className="space-y-3">
                    <img src={imagePreview} alt="Trip card preview" className="max-h-64 mx-auto rounded-lg shadow object-contain" />
                    <p className="text-sm text-slate-500">Click to change image</p>
                  </div>
                ) : (
                  <>
                    <Camera className="h-14 w-14 mx-auto text-slate-300 mb-3" />
                    <p className="text-base font-medium text-slate-700">Click to upload trip card photo</p>
                    <p className="text-sm text-slate-400 mt-1">Or use camera — JPEG, PNG, WEBP supported</p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="trip-card-file-input"
              />
              {imagePreview && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800">
                  <p className="font-medium mb-1">Ready to extract</p>
                  <p>GPT-4o Vision will read the handwritten card and extract all trip entries automatically. Please review the data before saving.</p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Review extracted data */}
          {step === 'reviewing' && extracted && (
            <div className="space-y-4 p-1">
              {/* Header info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Date</Label>
                  <Input
                    type="date"
                    value={extracted.date || ''}
                    onChange={e => setExtracted(ex => ({ ...ex, date: e.target.value }))}
                    className="mt-1 h-8 text-sm"
                    data-testid="scan-date"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Shift</Label>
                  <Select value={extracted.shift || ''} onValueChange={v => setExtracted(ex => ({ ...ex, shift: v }))}>
                    <SelectTrigger className="mt-1 h-8 text-sm" data-testid="scan-shift">
                      <SelectValue placeholder="Shift" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A">Shift A (4AM–12PM)</SelectItem>
                      <SelectItem value="B">Shift B (12PM–8PM)</SelectItem>
                      <SelectItem value="C">Shift C (8PM–4AM)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Door No (Truck)</Label>
                  <Input
                    value={extracted.door_no || ''}
                    onChange={e => setExtracted(ex => ({ ...ex, door_no: e.target.value }))}
                    className="mt-1 h-8 text-sm"
                    data-testid="scan-door-no"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Operator Name</Label>
                  <Input
                    value={extracted.operator_name || ''}
                    onChange={e => setExtracted(ex => ({ ...ex, operator_name: e.target.value }))}
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Starting KM</Label>
                  <Input
                    type="number"
                    value={extracted.starting_km ?? ''}
                    onChange={e => setExtracted(ex => ({ ...ex, starting_km: parseFloat(e.target.value) || null }))}
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Closing KM</Label>
                  <Input
                    type="number"
                    value={extracted.closing_km ?? ''}
                    onChange={e => setExtracted(ex => ({ ...ex, closing_km: parseFloat(e.target.value) || null }))}
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Starting HM</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={extracted.starting_hm ?? ''}
                    onChange={e => setExtracted(ex => ({ ...ex, starting_hm: parseFloat(e.target.value) || null }))}
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Closing HM</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={extracted.closing_hm ?? ''}
                    onChange={e => setExtracted(ex => ({ ...ex, closing_hm: parseFloat(e.target.value) || null }))}
                    className="mt-1 h-8 text-sm"
                  />
                </div>
              </div>

              {extracted.notes && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{extracted.notes}</span>
                </div>
              )}

              {/* Trips table */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold">Extracted Trips ({extracted.trips?.length})</Label>
                  <Button variant="outline" size="sm" onClick={addBlankTrip} className="h-7 text-xs">
                    <Plus className="h-3 w-3 mr-1" /> Add Row
                  </Button>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-100 border-b">
                        <th className="text-center px-2 py-2 w-10 text-slate-600">#</th>
                        <th className="text-left px-2 py-2 text-slate-600 w-24">L Time</th>
                        <th className="text-left px-2 py-2 text-slate-600 w-24">UL Time</th>
                        <th className="text-left px-2 py-2 text-slate-600 w-24">Machine No</th>
                        <th className="text-left px-2 py-2 text-slate-600">Material</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {extracted.trips.map((trip) => (
                        <tr key={trip._id} className={`border-b ${trip._edited ? 'bg-yellow-50' : ''}`}>
                          <td className="text-center px-2 py-1 text-slate-500 text-xs">{trip.serial}</td>
                          <td className="px-1 py-1">
                            <Input
                              value={trip.l_time || ''}
                              onChange={e => updateTrip(trip._id, 'l_time', e.target.value)}
                              className="h-7 text-xs"
                              placeholder="e.g. 12:07"
                            />
                          </td>
                          <td className="px-1 py-1">
                            <Input
                              value={trip.ul_time || ''}
                              onChange={e => updateTrip(trip._id, 'ul_time', e.target.value)}
                              className="h-7 text-xs"
                              placeholder="e.g. 12:14"
                            />
                          </td>
                          <td className="px-1 py-1">
                            <Input
                              value={trip.machine_no || ''}
                              onChange={e => updateTrip(trip._id, 'machine_no', e.target.value)}
                              className="h-7 text-xs"
                              placeholder="E-8"
                            />
                          </td>
                          <td className="px-1 py-1">
                            <Select
                              value={trip.material || materialTypes[0]}
                              onValueChange={v => updateTrip(trip._id, 'material', v)}
                            >
                              <SelectTrigger className="h-7 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {materialTypes.map(m => (
                                  <SelectItem key={m} value={m}>{m}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-1 py-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeTrip(trip._id)}
                              className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Done */}
          {step === 'done' && (
            <div className="flex flex-col items-center justify-center py-12 text-center space-y-3">
              <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-lg font-semibold text-slate-800">{savedCount} trips saved successfully!</p>
              <p className="text-sm text-slate-500">Door No. {extracted?.door_no} · {extracted?.date}</p>
              <Button variant="outline" onClick={() => { reset(); }} className="mt-2">
                Scan Another Card
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 pt-3 border-t">
          {step === 'upload' && (
            <>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button
                onClick={handleExtract}
                disabled={!imageFile || extracting}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="extract-trip-card-btn"
              >
                {extracting
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reading card...</>
                  : <><ScanLine className="h-4 w-4 mr-2" /> Extract Data with AI</>
                }
              </Button>
            </>
          )}
          {step === 'reviewing' && (
            <>
              <Button variant="outline" onClick={() => setStep('upload')}>
                <X className="h-4 w-4 mr-2" /> Re-scan
              </Button>
              <Button
                onClick={handleSaveAll}
                disabled={saving || !extracted?.trips?.length}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="save-all-trips-btn"
              >
                {saving
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                  : <><Check className="h-4 w-4 mr-2" /> Save All {extracted?.trips?.filter(t => t.l_time && t.ul_time).length} Trips</>
                }
              </Button>
            </>
          )}
          {step === 'done' && (
            <Button onClick={handleClose} className="bg-slate-900 hover:bg-slate-800">Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
